/**
 * useFaceFilters — MediaPipe Face Landmarker hook (Phase 3b — AR face filters).
 *
 * Loads MediaPipe's `FaceLandmarker` (from CDN, 2.4 MB WASM) and exposes a
 * `detect(video)` helper that returns the per-frame face landmarks in
 * NORMALIZED screen coordinates (0..1). Callers then draw their own overlays
 * (emoji glasses, crowns, mustaches, masks…) on top of the live preview / on
 * the canvas being baked into the recorded photo / video.
 *
 * The model file is fetched from MediaPipe's CDN the first time the hook is
 * used; the result is cached on the browser HTTP cache so subsequent opens of
 * the camera are instant. We deliberately do NOT bundle the model file —
 * 2.4 MB of WASM in our main bundle would inflate the Capacitor app size.
 */
import { useEffect, useRef, useState } from 'react';

const MODEL_URL = 'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task';
const WASM_BASE = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.35/wasm';

let _landmarkerPromise = null;

async function getLandmarker() {
  if (_landmarkerPromise) return _landmarkerPromise;
  _landmarkerPromise = (async () => {
    // Dynamic import keeps the 2 MB WASM out of the initial bundle.
    const vision = await import('@mediapipe/tasks-vision');
    const fileset = await vision.FilesetResolver.forVisionTasks(WASM_BASE);
    return vision.FaceLandmarker.createFromOptions(fileset, {
      // CPU delegate is much more compatible across iOS WebKit than GPU,
      // which fails silently on Safari/iOS and never produces detections.
      baseOptions: { modelAssetPath: MODEL_URL, delegate: 'CPU' },
      runningMode: 'VIDEO',
      numFaces: 1,
      outputFaceBlendshapes: false,
      outputFacialTransformationMatrixes: false,
    });
  })();
  return _landmarkerPromise;
}

export function useFaceLandmarker(enabled) {
  const landmarkerRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    // Reset readiness via the next microtask to avoid the React 19 purity
    // rule's "no setState in effect" warning on the immediate sync path.
    Promise.resolve().then(() => {
      if (!cancelled) { setReady(false); setError(null); }
    });
    getLandmarker()
      .then((lm) => {
        if (cancelled) return;
        landmarkerRef.current = lm;
        setReady(true);
      })
      .catch((e) => {
        if (!cancelled) setError(e);
      });
    return () => { cancelled = true; };
  }, [enabled]);

  /** Detect face landmarks from a HTMLVideoElement (current frame).
   *  Returns the first face's `keypoints` (array of {x,y} normalized 0..1)
   *  or null if no face is detected. */
  function detect(video, timestampMs) {
    const lm = landmarkerRef.current;
    if (!lm || !video || video.readyState < 2) return null;
    try {
      const res = lm.detectForVideo(video, timestampMs);
      const landmarks = res?.faceLandmarks?.[0];
      if (!landmarks || landmarks.length === 0) return null;
      return landmarks; // normalized 0..1 coordinates
    } catch (_e) {
      return null;
    }
  }

  return { ready, error, detect };
}

/**
 * The catalogue of AR face filters. Each filter is a draw function that
 * receives the 2D canvas context + the detected face landmarks + the
 * mirroring flag and renders the overlay. Coordinates are normalised 0..1,
 * we multiply by canvas.width / canvas.height before drawing.
 *
 * MediaPipe Face Mesh landmark indices we use here:
 *  - 10  : forehead centre (top)
 *  - 33  : right eye outer corner
 *  - 263 : left eye outer corner
 *  - 1   : nose tip
 *  - 152 : chin tip
 *  - 13  : upper lip centre
 *  - 14  : lower lip centre
 */
export const FACE_FILTERS = [
  { id: 'none',         label: 'Aucun',       draw: null },
  { id: 'sunglasses',   label: 'Lunettes',    draw: drawSunglasses },
  { id: 'crown',        label: 'Couronne',    draw: drawCrown },
  { id: 'hearts',       label: 'Cœurs',       draw: drawHeartEyes },
  { id: 'dog',          label: 'Chien',       draw: drawDogEars },
  { id: 'cat',          label: 'Chat',        draw: drawCat },
  { id: 'mustache',     label: 'Moustache',   draw: drawMustache },
  { id: 'mask',         label: 'Masque',      draw: drawMask },
];

function drawEmojiAt(ctx, emoji, cxPx, cyPx, sizePx) {
  ctx.save();
  ctx.font = `${sizePx}px "Apple Color Emoji","Noto Color Emoji","Segoe UI Emoji",sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(emoji, cxPx, cyPx);
  ctx.restore();
}

function eyeDistancePx(landmarks, w, h) {
  const r = landmarks[33];
  const l = landmarks[263];
  const dx = (r.x - l.x) * w;
  const dy = (r.y - l.y) * h;
  return Math.hypot(dx, dy) || 80;
}

function drawSunglasses(ctx, landmarks, w, h) {
  const eyeDist = eyeDistancePx(landmarks, w, h);
  const r = landmarks[33], l = landmarks[263];
  const cx = ((r.x + l.x) / 2) * w;
  const cy = ((r.y + l.y) / 2) * h;
  // 🕶 sized roughly 2× the eye-distance for a believable fit
  drawEmojiAt(ctx, '🕶️', cx, cy, eyeDist * 2.0);
}

function drawCrown(ctx, landmarks, w, h) {
  const eyeDist = eyeDistancePx(landmarks, w, h);
  const fhead = landmarks[10];
  drawEmojiAt(ctx, '👑', fhead.x * w, fhead.y * h - eyeDist * 0.5, eyeDist * 1.6);
}

function drawHeartEyes(ctx, landmarks, w, h) {
  const eyeDist = eyeDistancePx(landmarks, w, h);
  drawEmojiAt(ctx, '💖', landmarks[33].x * w, landmarks[33].y * h, eyeDist * 0.7);
  drawEmojiAt(ctx, '💖', landmarks[263].x * w, landmarks[263].y * h, eyeDist * 0.7);
}

function drawDogEars(ctx, landmarks, w, h) {
  const eyeDist = eyeDistancePx(landmarks, w, h);
  const fhead = landmarks[10];
  // Single tongue+ears emoji over the head — simple and reliable across devices.
  drawEmojiAt(ctx, '🐶', fhead.x * w, fhead.y * h - eyeDist * 0.6, eyeDist * 1.9);
}

function drawCat(ctx, landmarks, w, h) {
  const eyeDist = eyeDistancePx(landmarks, w, h);
  const fhead = landmarks[10];
  drawEmojiAt(ctx, '🐱', fhead.x * w, fhead.y * h - eyeDist * 0.6, eyeDist * 1.9);
}

function drawMustache(ctx, landmarks, w, h) {
  const eyeDist = eyeDistancePx(landmarks, w, h);
  // Between nose (1) and upper lip (13)
  const n = landmarks[1], u = landmarks[13];
  const cx = ((n.x + u.x) / 2) * w;
  const cy = ((n.y + u.y) / 2) * h;
  drawEmojiAt(ctx, '👨‍🦱', cx, cy, eyeDist * 0.0); // placeholder hidden
  drawEmojiAt(ctx, 'ඞ̃', cx, cy, eyeDist * 0.0); // placeholder hidden
  drawEmojiAt(ctx, '〰️', cx, cy, eyeDist * 1.3);
}

function drawMask(ctx, landmarks, w, h) {
  const eyeDist = eyeDistancePx(landmarks, w, h);
  const fhead = landmarks[10], chin = landmarks[152];
  const cx = ((fhead.x + chin.x) / 2) * w;
  const cy = ((fhead.y + chin.y) / 2) * h;
  drawEmojiAt(ctx, '🎭', cx, cy, eyeDist * 2.6);
}
